import { QueryClient, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import { useDeepCompareEffect, useShallowCompareEffect } from "react-use"

import type { TEmptyAsyncCallback } from "@/shared/types/functions"

import { ECoachingChatQueryKey as EQueryKey } from "../config"
import {
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    type ICoachingChatConversationQueryDto as IConversationDto,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"

type _TSeedsParams = Pick<_TSharedParams, "entityId"> & { onComplete(areCompleted: boolean): Promise<void> }
function useSeedsCompletor({ entityId, onComplete }: _TSeedsParams): void {
    const formState: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const isNotAvailable: boolean = eq(conversationStatus, EConversationStatus.NotAvailable)

    useDeepCompareEffect((): void => {
        if (!onComplete) return void 0

        if (isNotAvailable && (formState.isFilled || formState.isSubmitted)) return void onComplete(formState.isFilled)

        return void onComplete(formState.isSubmitted)
    }, [formState.isSubmitted, formState.isFilled, isNotAvailable, onComplete])
}

type _TChatParams = Pick<_TSharedParams, "entityId"> & { onComplete: TEmptyAsyncCallback | ((date: string) => void) }
function useChatCompletor({ entityId, onComplete }: _TChatParams): void {
    const qc: QueryClient = useQueryClient()

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)
    const conversationId: number | null = useConversationIdSelector(entityId)

    const shouldRunBasedOnStatus: boolean = includes<EConversationStatus>(
        [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
        conversationStatus
    )

    useShallowCompareEffect((): void => {
        const queryData: IConversationDto | undefined = qc.getQueryData<IConversationDto>(
            EQueryKey.Conversation(conversationId)
        )

        if (shouldRunBasedOnStatus && !!queryData) void onComplete(queryData.completed_stamp)
    }, [shouldRunBasedOnStatus, conversationId, onComplete])
}

export { useSeedsCompletor as useCoachingChatSeedsCompletor, useChatCompletor as useCoachingChatCompletor }
